import React, { useState, useRef, useEffect } from 'react';
import './VideoPlayer.css';
import { VideoOptimizer, ProgressiveVideoLoader } from './VideoOptimizer';
import { VideoUtils } from '../utils/videoUtils';

const VideoPlayer = ({ 
  src = '/edulab-video.mp4', 
  title = 'How Edulab UUMS Powers Modern University',
  autoplay = false,
  controls = true,
  className = '',
  useYouTube = false,
  youtubeId = null
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [videoSources, setVideoSources] = useState([]);
  const videoRef = useRef(null);

  // Define fallback video sources and check accessibility
  useEffect(() => {
    const fallbackSources = [
      src,
      '/edulab-video.mp4',
      'https://edulab.in/edulab-video.mp4',
      'https://www.edulab.in/edulab-video.mp4'
    ];
    setVideoSources(fallbackSources);

    // Check video accessibility
    const checkVideoAccess = async () => {
      try {
        const result = await VideoUtils.checkVideoAccessibility(src);
        if (!result.accessible) {
          console.warn('Primary video source not accessible:', result);
        }
      } catch (error) {
        console.warn('Could not check video accessibility:', error);
      }
    };

    checkVideoAccess();
  }, [src]);

  const handleLoadStart = () => {
    setIsLoading(true);
    setHasError(false);
  };

  const handleLoadedData = () => {
    setIsLoading(false);
    setHasError(false);
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleError = (e) => {
    console.error('Video loading error:', e);
    setIsLoading(false);
    setHasError(true);
    
    // Try next fallback source
    if (retryCount < videoSources.length - 1) {
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setHasError(false);
        setIsLoading(true);
        if (videoRef.current) {
          videoRef.current.src = videoSources[retryCount + 1];
          videoRef.current.load();
        }
      }, 1000);
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    setHasError(false);
    setIsLoading(true);
    if (videoRef.current) {
      videoRef.current.src = videoSources[0];
      videoRef.current.load();
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const videoSettings = VideoOptimizer.getOptimalVideoSettings();

  // YouTube embed component
  if (useYouTube && youtubeId) {
    return (
      <div className={`video-player-container ${className}`}>
        <div className="video-info">
          <h3 className="video-title">{title}</h3>
          <p className="video-description">
            Discover how Edulab UUMS is revolutionizing modern university management systems.
          </p>
        </div>
        
        <div className="video-wrapper youtube-wrapper">
          <iframe
            width="100%"
            height="315"
            src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
            title={title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="youtube-iframe"
          ></iframe>
        </div>
      </div>
    );
  }

  return (
    <div className={`video-player-container ${className}`}>
      <div className="video-info">
        <h3 className="video-title">{title}</h3>
        <p className="video-description">
          Discover how Edulab UUMS is revolutionizing modern university management systems.
        </p>
      </div>
      
      <div className="video-wrapper">
        <ProgressiveVideoLoader src={videoSources[retryCount] || src}>
          <video
            ref={videoRef}
            title={title}
            controls={controls}
            autoPlay={autoplay}
            onLoadStart={handleLoadStart}
            onLoadedData={handleLoadedData}
            onTimeUpdate={handleTimeUpdate}
            onPlay={handlePlay}
            onPause={handlePause}
            onError={handleError}
            className="video-element"
            preload={videoSettings.preload}
            poster={videoSettings.poster}
            crossOrigin="anonymous"
          >
            <source src={videoSources[retryCount] || src} type="video/mp4" />
            {videoSources.map((source, index) => (
              index !== retryCount && (
                <source key={index} src={source} type="video/mp4" />
              )
            ))}
            Your browser does not support the video tag.
          </video>
        </ProgressiveVideoLoader>
        
        {isLoading && (
          <div className="video-loading">
            <div className="loading-spinner"></div>
            <p>Loading video...</p>
            {retryCount > 0 && (
              <p className="retry-notice">Trying alternative source ({retryCount + 1}/{videoSources.length})</p>
            )}
          </div>
        )}

        {hasError && retryCount >= videoSources.length - 1 && (
          <div className="video-error-overlay">
            <div className="error-content">
              <i className="fas fa-exclamation-triangle error-icon"></i>
              <h3>Unable to load video</h3>
              <p>We're having trouble loading the video. This could be due to:</p>
              <ul className="error-reasons">
                <li>Slow internet connection</li>
                <li>Video file temporarily unavailable</li>
                <li>Browser compatibility issues</li>
              </ul>
              <div className="error-actions">
                <button onClick={handleRetry} className="retry-button">
                  <i className="fas fa-redo"></i> Try Again
                </button>
                <button onClick={() => window.location.reload()} className="refresh-button">
                  <i className="fas fa-refresh"></i> Refresh Page
                </button>
              </div>
              <p className="error-help">
                If the problem persists, please check your internet connection or try again later.
              </p>
            </div>
          </div>
        )}

        {!controls && (
          <div className="custom-controls">
            <div className="play-button" onClick={togglePlay}>
              {isPlaying ? (
                <i className="fas fa-pause"></i>
              ) : (
                <i className="fas fa-play"></i>
              )}
            </div>
            
            <div className="progress-container" onClick={handleSeek}>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                ></div>
              </div>
            </div>
            
            <div className="time-display">
              <span>{formatTime(currentTime)}</span>
              <span>/</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
